import * as React from 'react';
import { observer } from 'mobx-react';

import {
    ConnectApiRateBundleDto,
    ConnectApiRateBundleTermDto
} from '../../core/api/dtos';
import { Markdown } from '../markdown';
import './rate-bundles-list.scss';
import { RateBundleTerm } from '../rate-bundle-term';
import { RateBundlesRequest } from '../../core/rate-bundle-util';

interface RateBundlesListProps {
    rateBundlesRequest: RateBundlesRequest;
    onClickTerm(
        rateBundle: ConnectApiRateBundleDto,
        term: ConnectApiRateBundleTermDto
    ): void;
}

export const RateBundlesList = observer((props: RateBundlesListProps) => {
    const { rateBundlesRequest, onClickTerm } = props;
    const [focusedBundle, setFocusedBundle] = React.useState<
        number | undefined
    >();

    return (
        <div
            className={classnames('rate-bundles-list', {
                'rate-bundles-list--single':
                    rateBundlesRequest.result?.length === 1
            })}
            id="rate-bundles-list"
        >
            {rateBundlesRequest.match({
                fetched: (data) =>
                    data.map((bundle) => (
                        <RateBundleItem
                            key={bundle.id}
                            bundle={bundle}
                            focusedBundle={focusedBundle}
                            toggleFocused={() =>
                                setFocusedBundle(
                                    focusedBundle ? undefined : bundle.id
                                )
                            }
                            onClickTerm={onClickTerm}
                        />
                    ))
            })}
            <div className="rate-bundles-list__grid-item" />
            <article
                className={classnames('ref-overlay', { active: focusedBundle })}
                onClick={() => setFocusedBundle(undefined)}
            />
        </div>
    );
});

interface RateBundleItemProps {
    bundle: ConnectApiRateBundleDto;
    focusedBundle: number | undefined;
    toggleFocused(): void;
    onClickTerm(
        rateBundle: ConnectApiRateBundleDto,
        term: ConnectApiRateBundleTermDto
    ): void;
}

const RateBundleItem = observer(
    ({
        bundle,
        focusedBundle,
        toggleFocused,
        onClickTerm
    }: RateBundleItemProps) => {
        const focused = focusedBundle === bundle.id;
        const active = !focusedBundle || focused;

        return (
            <div
                className={classnames('rate-bundles-list__grid-item', {
                    'rate-bundles-list__grid-item--toggled': focused
                })}
                style={{ padding: '0 4px' }}
                data-role="rate-bundle-card"
                data-role-value={bundle.name}
            >
                <fieldset
                    className={classnames(
                        'rate-bundle-item',
                        {
                            'rate-bundle-item--toggled': focused
                        },
                        {
                            'rate-bundle-item--active': active
                        }
                    )}
                >
                    <button
                        className={classnames('rate-bundles-list__min-max', {
                            toggled: focused
                        })}
                        type="button"
                        onClick={toggleFocused}
                    />
                    <div
                        className={classnames(
                            'rate-bundle-item__toggle-wrapper',
                            {
                                toggled: focused
                            }
                        )}
                        style={focused ? { height: '100%' } : undefined}
                        onClick={toggleFocused}
                    >
                        <div className="info">
                            <h2>{bundle.name}</h2>
                            <p></p>
                        </div>
                        <div className="listc-wrapper">
                            {bundle.description && <p>{bundle.description}</p>}
                            {bundle.subDescription && (
                                <Markdown listType="listc_module">
                                    {bundle.subDescription}
                                </Markdown>
                            )}
                        </div>
                    </div>
                    <ul className="rate-bundle-item__terms ">
                        {bundle.terms.map((term, index) => (
                            <RateBundleTerm
                                key={term.id}
                                index={index}
                                term={term}
                                footnote={bundle.footnote}
                                onClick={() => onClickTerm(bundle, term)}
                            />
                        ))}
                    </ul>
                </fieldset>
            </div>
        );
    }
);
